import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import validator from 'validator';
import { Button, Slider, Form, Select, Input } from 'antd';
import { EditorElementPopover } from '../EditorElementPopover';
import { extraColors } from '../../../constants/extraColors';
import { faSolidIcons, faBrandIcons } from '../../../constants/faIcons';
import {
  SwapOutlined,
  StopOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const FormItem = Form.Item;

export class PIcon extends React.Component {
  constructor(props) {
    super(props);
    this.popoverRef = React.createRef();
  }

  state = {
    showBtn: false,

    targetValue: this.props.target,
    targetValidateStatus: 'success',
    targetHelp: null,
  };

  static propTypes = {
    // User editable properties
    size: PropTypes.string, // xxs | xs | sm | md (default) | lg | xl | xxl

    // primary, accent(1-3), | bgLight | bgDark | bgLightAlt | bgDarkAlt, black-hi, black-md, black-lo, white-hi, white-md, white-lo
    color: PropTypes.string,
    name: PropTypes.string, // "star"

    // default properties
    className: PropTypes.string,
    style: PropTypes.object,

    // none (default) | url | email | phone
    targetType: PropTypes.string,
    target: PropTypes.string,

    // required properties
    colorScheme: PropTypes.object,

    address: PropTypes.array,
    updateNode: PropTypes.func,
    invokeIconChooser: PropTypes.func,
    publish: PropTypes.bool,
  };

  static defaultProps = {
    name: 'star',
    size: 'md',
    color: 'primary',
    publish: false,

    targetType: 'none',
    target: null,
  };

  marks = {
    0: 'xxs',
    10: 'xs',
    20: 'sm',
    30: 'md',
    40: 'lg',
    50: 'xl',
    60: 'xxl',
  };

  handleIconClick = () => {
    this.popoverRef.current.dismiss();
    this.props.invokeIconChooser(this.props.address, 'name');
  };

  handleIconSizeChange = (value) => {
    const nextSize = this.marks[value];
    this.props.updateNode(this.props.address, { size: nextSize });
  };

  iconColorSelected = (color) => {
    this.props.updateNode(this.props.address, { color });
  };

  makePill = (c) => {
    let color = null;
    if (c in this.props.colorScheme) {
      color = this.props.colorScheme[c];
    }
    else {
      color = extraColors[c];
    }

    return (
      <span
        onClick={() => this.iconColorSelected(c)}
        className={cx(
          'bgpill',
          { selected: (c === this.props.color) }
        )}
        style={{ backgroundColor: color }}
      />
    );
  };

  getTargetPlaceholder = () => {
    const { targetType } = this.props;
    if (targetType === 'url') {
      return 'Enter a URL';
    }
    if (targetType === 'email') {
      return 'Enter an email';
    }
    if (targetType === 'phone') {
      return 'Enter a phone';
    }
    return 'Link is disabled';
  };

  validateTargetAsPhone = () => {
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  validateTargetAsEmail = (email) => {
    const validated = validator.isEmail(email);
    if (!validated) {
      this.setState({
        targetValidateStatus: 'warning',
        targetHelp: 'Enter a valid email',
      });
      return false;
    }
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  validateTargetAsUrl = (url) => {
    if (!validator.isURL(url)) {
      this.setState({
        targetValidateStatus: 'warning',
        targetHelp: 'Enter a valid url',
      });
      return false;
    }
    this.setState({
      targetValidateStatus: 'success',
      targetHelp: null,
    });
    return true;
  }

  targetChanged = (e) => {
    const { value } = e.target;
    const target = value.trim();
    const { targetType } = this.props;

    let validated = false;
    if (targetType === 'url') {
      validated = this.validateTargetAsUrl(target);
    }
    else if (targetType === 'email') {
      validated = this.validateTargetAsEmail(target);
    }
    else if (targetType === 'phone') {
      validated = this.validateTargetAsPhone(target);
    }
    if (validated) {
      this.props.updateNode(this.props.address, { target });
    }

    this.setState({ targetValue: target });
  }

  targetTypeChanged = (activeKey) => {
    if (activeKey === this.props.targetType) {
      return;
    }
    this.props.updateNode(this.props.address, {
      targetType: activeKey,
      target: '',
    });
    this.setState({ targetValue: null });
  };

  popoverContent = () => (
    <React.Fragment>

      <Button
        type="primary" icon={<SwapOutlined />} block
        onClick={this.handleIconClick}
      >
        Change icon
      </Button>

      <div className="divider" />

      <div className="label">
        <div className="main">Color</div>
      </div>
      <div className="bgpillContainer mb-2 text-center">
        {this.makePill('primary')}
        {this.makePill('accent1')}
        {this.makePill('accent2')}
        {this.makePill('accent3')}
        {this.makePill('bgLight')}
        {this.makePill('bgLightAlt')}
        {this.makePill('bgDark')}
        {this.makePill('bgDarkAlt')}
      </div>
      <div className="bgpillContainer text-center">
        {this.makePill('black-hi')}
        {this.makePill('black-md')}
        {this.makePill('black-lo')}
        {this.makePill('white-hi')}
        {this.makePill('white-md')}
        {this.makePill('white-lo')}
      </div>

      <div className="divider" />

      <div className="label">
        <div className="main">
          Link
        </div>
      </div>
      <FormItem
        validateStatus={this.state.targetValidateStatus}
        hasFeedback={false}
        help={this.state.targetHelp}
      >
        <Input
          addonBefore={
            <Select
              defaultValue={
                (this.props.targetType === null || this.props.targetType) === 'none' ? 'none' : this.props.targetType
              }
              onChange={this.targetTypeChanged}
            >
              <Select.Option value="none"><StopOutlined /></Select.Option>
              <Select.Option value="url"><LinkOutlined /></Select.Option>
              <Select.Option value="email"><MailOutlined /></Select.Option>
              <Select.Option value="phone"><PhoneOutlined /></Select.Option>
            </Select>
          }
          disabled={this.props.targetType === 'none' || this.props.targetType === null}
          onChange={this.targetChanged}
          value={this.state.targetValue}
          placeholder={this.getTargetPlaceholder()}
        />
      </FormItem>

      <div className="divider" />

      <div className="label">
        <div className="main">
          Size
        </div>
      </div>
      <div>
        <Slider
          marks={this.marks}
          step={null}
          min={0} max={60}
          defaultValue={30}
          tipFormatter={null}
          onAfterChange={this.handleIconSizeChange}
        />
      </div>
    </React.Fragment>
  );

  isSizeSelected = (csize) => (
    (this.props.size === csize) ? <CheckOutlined /> : null
  );

  onMouseOver = (e) => {
    this.setState({ showBtn: true });
    e.stopPropagation();
  };

  onMouseOut = (e) => {
    this.setState({ showBtn: false });
    e.stopPropagation();
  };

  getSolidColor = (c) => {
    const { colorScheme } = this.props;
    const mycolor = (typeof c === 'undefined') ? 'primary' : c;
    if (mycolor in colorScheme) {
      return colorScheme[mycolor];
    }
    return extraColors[mycolor];
  };

  render() {
    const { color, name, size, publish, target, targetType } = this.props;

    const styleInEditor = publish ? {} : {
      color: this.getSolidColor(color),
    };

    const iconInnerNode = (
      <i
        className={cx(
          {
            fas: faSolidIcons.includes(name),
            fab: faBrandIcons.includes(name),
          },
          `fa-${name}`,
          `size-${size}`,
          `color-${color}`,
        )}
        style={{
          ...this.props.style,
          ...styleInEditor,
        }}
      />
    );

    let hrefVal = '';
    if (targetType !== null && target !== null && target.length > 0) {
      hrefVal = target;
      if (targetType === 'email') {
        hrefVal = `mailto:${hrefVal}`;
      }
      else if (targetType === 'phone') {
        hrefVal = `tel:${hrefVal}`;
      }
    }

    let iconNode = null;
    if (hrefVal.length > 0) {
      iconNode = (
        <a href={hrefVal}>
          { iconInnerNode }
        </a>
      );
    }
    else {
      iconNode = iconInnerNode;
    }

    return (
      <div
        className={cx(
          'picon',
          this.props.className,
        )}
        onMouseOver={this.onMouseOver}
        onFocus={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onBlur={this.onMouseOut}
      >

        { iconNode }

        <EditorElementPopover
          ref={this.popoverRef}
          usecase="icon"
          showBtn={this.state.showBtn}
          btnLabel="icon"
          title="Icon appearance"
          content={this.popoverContent()}
          placement="bottomLeft"
          publish={publish}
        />

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/editor/elements/PIcon.jsx